// Co-occurrence Network visualization using vis-network
// ノードは DataSet で保持し、色モード切替では再レイアウトせず色だけ差し替える
import { Network, DataSet } from 'vis-network/standalone'
import { resolveSentimentColors } from './sentiment_colors'

class CoOccurNetworkRenderer {
  constructor() {
    this.init()
  }

  init() {
    document.addEventListener('turbo:load', () => {
      this.renderNetworks()
    })
    // 初回ロード時のため
    if (document.readyState !== 'loading') {
      this.renderNetworks()
    }
  }

  renderNetworks() {
    // data-co-occur-* 属性を持つ要素を処理
    const elements = document.querySelectorAll('[data-co-occur-networks]')
    
    elements.forEach((element) => {
      try {
        const wordsData = JSON.parse(element.getAttribute('data-co-occur-words') || '[]')
        const featuresData = JSON.parse(element.getAttribute('data-co-occur-features') || '{}')
        const networksData = JSON.parse(element.getAttribute('data-co-occur-networks') || '[]')
        const labelsData = JSON.parse(element.getAttribute('data-co-occur-labels') || '[]')
        // data-sentiment-classes( { 語: "positive" } )は CSS 変数を扱えないので実色に解決してから使う
        const sentimentColorsData = resolveSentimentColors(JSON.parse(element.getAttribute('data-sentiment-classes') || 'null'))

        this.createNetwork(element, {
          words: wordsData,
          features: featuresData,
          networks: networksData,
          labels: labelsData,
          sentimentColors: sentimentColorsData
        })
      } catch (error) {
        console.error('Co-occur network data parsing error:', error)
        element.innerHTML = '<div class="h-full flex items-center justify-center"><p class="text-red-500 p-4 text-center">データの解析エラー</p></div>'
      }
    })
  }

  createNetwork(element, data) {
    const { words, features, networks, labels, sentimentColors } = data

    if (!networks || networks.length === 0) {
      element.innerHTML = '<div class="h-full flex items-center justify-center"><p class="text-base-content/70 text-center">ネットワークデータがありません</p></div>'
      return
    }

    // Clear existing content
    element.innerHTML = ''

    // Create nodes
    const nodeItems = this.createNodes(words, features, labels, sentimentColors)

    // Create edges
    const edges = this.createEdges(networks, nodeItems)

    if (nodeItems.length === 0) {
      element.innerHTML = '<div class="h-full flex items-center justify-center"><p class="text-base-content/70 text-center">ノードデータがありません</p></div>'
      return
    }

    console.log('Nodes with labels:', nodeItems.slice(0, 3)) // デバッグ用

    try {
      // Network configuration
      // 色モード切替で nodes.update できるよう DataSet にして要素に保持する
      const nodes = new DataSet(nodeItems)
      element._coOccurNodes = nodes
      const networkData = { nodes, edges }
      const options = this.getNetworkOptions()

      // Create network
      const network = new Network(element, networkData, options)

      // Handle stabilization
      network.on("stabilizationIterationsDone", () => {
        network.setOptions({ physics: false })
        // 物理演算停止後にラベル表示を強制
        network.setOptions({
          nodes: {
            font: {
              size: 12,
              face: 'Arial, sans-serif',
              color: '#000000',
              background: 'rgba(255, 255, 255, 0.9)',
              strokeWidth: 2,
              strokeColor: '#ffffff'
            }
          }
        })
        network.redraw()
      })
      
      // 初期化直後にもラベル表示を強制
      setTimeout(() => {
        network.setOptions({
          nodes: {
            font: {
              size: 12,
              face: 'Arial, sans-serif', 
              color: '#000000',
              background: 'rgba(255, 255, 255, 0.9)',
              strokeWidth: 2,
              strokeColor: '#ffffff'
            }
          }
        })
        network.redraw()
      }, 500)
      
    } catch (error) {
      console.error('Network render error:', error)
      element.innerHTML = '<div class="h-full flex items-center justify-center"><p class="text-red-500 p-4 text-center">ネットワーク描画エラー</p></div>'
    }
  }

  createNodes(words, features, labels, sentimentColors = null) {
    const nodes = []
    const wordsMap = {}

    // Create words map for quick lookup
    words.forEach(([word, count]) => {
      wordsMap[word] = count
    })

    labels.forEach((word, index) => {
      if (wordsMap[word]) {
        const hinshi = features[word] || '名詞'
        const color = this.getHinshiColor(hinshi)
        const sentimentColor = sentimentColors ? sentimentColors[word] : null

        nodes.push({
          id: index + 1,
          label: word,
          // title: word, // ツールチップ用
          value: wordsMap[word],
          // 色モード切替で使う色源。感情色が無い語は品詞色のまま
          hinshiColor: color,
          sentimentColor: sentimentColor || null,
          color: {
            background: color,
            border: this.darkenColor(color, 0.2)
          },
          font: { 
            color: '#000000',
            size: 32,
            face: 'Arial, sans-serif',
            strokeWidth: 2,
            strokeColor: '#ffffff',
            background: 'rgba(255, 255, 255, 0.9)'
          },
          physics: true,
        })
      }
    })

    return nodes
  }

  createEdges(networks, nodes) {
    const edges = []
    const maxSumNum = Math.max(...networks.map(n => n.sum_num))
    
    networks.forEach(network => {
      const fromNode = nodes.find(n => n.label === network.words[0])
      const toNode = nodes.find(n => n.label === network.words[1])
      
      if (fromNode && toNode) {
        const edgeValue = network.sum_num / maxSumNum
        edges.push({
          from: fromNode.id,
          to: toNode.id,
          value: edgeValue,
          width: Math.max(edgeValue * 8, 1),
          color: {
            color: '#167bff',
            opacity: 0.6
          },
          smooth: {
            enabled: false
          }
        })
      }
    })

    return edges
  }

  getNetworkOptions() {
    return {
      nodes: {
        shape: "dot",
        scaling: {
          min: 20,
          max: 50
        },
        font: {
          size: 12,
          face: 'Arial, sans-serif',
          color: '#000000',
          background: 'rgba(255, 255, 255, 0.9)',
          strokeWidth: 2,
          strokeColor: '#ffffff',
          align: 'center'
        },
        borderWidth: 2,
        shadow: true,
        labelHighlightBold: false,
        chosen: false
      },
      edges: {
        scaling: {
          min: 1,
          max: 8
        },
        smooth: {
          enabled: false
        }
      },
      physics: {
        forceAtlas2Based: {
          gravitationalConstant: -200,
          springLength: 230,
          springConstant: 0.18,
          avoidOverlap: 3
        },
        maxVelocity: 146,
        solver: 'forceAtlas2Based',
        timestep: 0.35,
        stabilization: {
          enabled: true,
          iterations: 1000,
          updateInterval: 25
        }
      },
      interaction: {
        hover: false,
        tooltipDelay: 200,
        zoomView: true,
        dragView: true,
        selectConnectedEdges: false,
        hideEdgesOnDrag: false,
        hideNodesOnDrag: false
      },
      layout: {
        improvedLayout: true
      },
      configure: {
        enabled: false
      },
      autoResize: true
    }
  }

  // 色モード切替。再レイアウトせずノードの色だけ差し替える
  applyColorMode(mode) {
    document.querySelectorAll('[data-co-occur-networks]').forEach((element) => {
      const nodes = element._coOccurNodes
      if (!nodes) return

      const updates = nodes.get().map((node) => {
        const color = mode === 'sentiment' && node.sentimentColor ? node.sentimentColor : node.hinshiColor
        return { id: node.id, color: { background: color, border: this.darkenColor(color, 0.2) } }
      })
      nodes.update(updates)
    })
  }

  getHinshiColor(hinshi) {
    // ApplicationController::HINSHI_COLORS に対応
    const hinshiColors = {
      '名詞': '#3498DB',
      '形容詞': '#26B99A',
      'オリジナル': '#d9534f'
    }
    
    return hinshiColors[hinshi] || '#6B7280'
  }

  // Color utility functions
  darkenColor(color, amount) {
    const hex = color.replace('#', '')
    const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - Math.round(255 * amount))
    const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - Math.round(255 * amount))
    const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - Math.round(255 * amount))
    
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
  }

  lightenColor(color, amount) {
    const hex = color.replace('#', '')
    const r = Math.min(255, parseInt(hex.substr(0, 2), 16) + Math.round(255 * amount))
    const g = Math.min(255, parseInt(hex.substr(2, 2), 16) + Math.round(255 * amount))
    const b = Math.min(255, parseInt(hex.substr(4, 2), 16) + Math.round(255 * amount))
    
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
  }
}

// 自動初期化
const coOccurNetworkRenderer = new CoOccurNetworkRenderer()

// 「感情で色分け」トグルの切り替えを受けてノード色を差し替える
document.addEventListener('text-mining:color-mode', (event) => {
  const mode = event.detail && event.detail.mode === 'sentiment' ? 'sentiment' : 'hinshi'
  coOccurNetworkRenderer.applyColorMode(mode)
})
